import { loadImageFromFile, canvasToBlob } from "@/lib/imageProcessing";

export type CropAspect = "original" | "1:1" | "4:5" | "16:9";

export const ASPECT_RATIOS: Record<CropAspect, number | null> = {
  original: null,
  "1:1": 1,
  "4:5": 4 / 5,
  "16:9": 16 / 9,
};

/**
 * Centre-crops an image file to the chosen aspect ratio with an optional zoom,
 * baking the selected CSS filter in so what you preview is what gets posted.
 */
export async function cropImageFile(
  file: File,
  { aspect, zoom = 1, cssFilter = "none" }: { aspect: CropAspect; zoom?: number; cssFilter?: string },
): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  const ratio = ASPECT_RATIOS[aspect];
  if (!ratio && zoom === 1 && (cssFilter === "none" || !cssFilter)) return file;

  const img = await loadImageFromFile(file);
  const targetRatio = ratio ?? img.width / img.height;

  // Source rectangle: largest region of the image matching the target ratio, scaled by zoom.
  let sw = img.width;
  let sh = img.width / targetRatio;
  if (sh > img.height) {
    sh = img.height;
    sw = img.height * targetRatio;
  }
  sw /= zoom;
  sh /= zoom;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(sw);
  canvas.height = Math.round(sh);
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  if (cssFilter && cssFilter !== "none") ctx.filter = cssFilter;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

  const blob = await canvasToBlob(canvas, "image/jpeg", 0.92);
  return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
}
